import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
} from '@mui/material';
import videoCreatorApi from '../../utils/videoCreatorApi';
import { getVideoCreatorBaseUrl } from '../../utils/videoCreatorApi';

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function mediaSrc(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  // Same-origin absolute /api/... paths
  if (url.startsWith('/api/')) return url;
  const base = getVideoCreatorBaseUrl().replace(/\/+$/, '');
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}

/** Lightweight markdown-ish display (headings, lists, paragraphs) without extra deps. */
function SimpleMarkdown({ text }) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('### ')) {
      blocks.push(
        <Typography key={i} variant="subtitle1" fontWeight={700} sx={{ mt: 2, mb: 0.5 }}>
          {line.slice(4)}
        </Typography>
      );
      i += 1;
    } else if (line.startsWith('## ')) {
      blocks.push(
        <Typography key={i} variant="h6" fontWeight={700} sx={{ mt: 2.5, mb: 0.5 }}>
          {line.slice(3)}
        </Typography>
      );
      i += 1;
    } else if (line.startsWith('# ')) {
      blocks.push(
        <Typography key={i} variant="h5" fontWeight={700} sx={{ mt: 1, mb: 1 }}>
          {line.slice(2)}
        </Typography>
      );
      i += 1;
    } else if (line.startsWith('| ') && line.includes('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        tableLines.push(lines[i]);
        i += 1;
      }
      const rows = tableLines
        .filter((r) => !/^\|\s*-+/.test(r))
        .map((r) =>
          r
            .split('|')
            .slice(1, -1)
            .map((c) => c.trim())
        );
      blocks.push(
        <Box key={`t-${i}`} component="table" sx={{ width: '100%', borderCollapse: 'collapse', my: 1 }}>
          <tbody>
            {rows.map((cells, ri) => (
              <tr key={ri}>
                {cells.map((c, ci) => (
                  <Box
                    key={ci}
                    component={ri === 0 ? 'th' : 'td'}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      px: 1,
                      py: 0.5,
                      textAlign: 'left',
                      fontWeight: ri === 0 ? 700 : 400,
                      fontSize: '0.875rem',
                    }}
                  >
                    {c}
                  </Box>
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      );
    } else if (line.startsWith('- ') || /^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || /^\d+\.\s/.test(lines[i]))) {
        items.push(lines[i].replace(/^(- |\d+\.\s)/, ''));
        i += 1;
      }
      blocks.push(
        <Box key={`ul-${i}`} component="ul" sx={{ pl: 3, my: 1 }}>
          {items.map((it, idx) => (
            <Typography key={idx} component="li" variant="body2" sx={{ mb: 0.5 }}>
              {it}
            </Typography>
          ))}
        </Box>
      );
    } else if (line.trim() === '' || line.trim() === '---') {
      i += 1;
    } else {
      blocks.push(
        <Typography key={i} variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
          {line}
        </Typography>
      );
      i += 1;
    }
  }
  return <Box>{blocks}</Box>;
}

export default function HelpContacts() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [help, setHelp] = useState(null);
  const [genNotes, setGenNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState('');
  const [genErr, setGenErr] = useState('');

  const loadHelp = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await videoCreatorApi.get('/v1/modules/contacts');
      setHelp(r.data?.data || null);
    } catch (e) {
      setError(
        e.response?.data?.message ||
          'Help service unreachable. Ensure erp4c-video-creator is deployed (videoCreator.enabled) and rebuild that image.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHelp();
  }, [loadHelp]);

  const pollJob = async (jobId) => {
    // Full narrated Contacts tutorial can take ~8–15 minutes (UI + TTS + mux).
    for (let i = 0; i < 450; i += 1) {
      await new Promise((r) => setTimeout(r, 2000));
      const jr = await videoCreatorApi.get(`/v1/jobs/${jobId}`);
      const job = jr.data?.data;
      if (!job) continue;
      if (job.status === 'succeeded' || job.status === 'completed') return job;
      if (job.status === 'failed' || job.status === 'errored') {
        throw new Error(job.error || 'Generate failed');
      }
    }
    throw new Error('Generate timed out — check video-creator logs and refresh this tab.');
  };

  const generateVersion = async () => {
    setGenerating(true);
    setGenErr('');
    setGenMsg(
      'Queued… recording full Contacts tutorial with voiceover (filters, create, accounts, documents — often 8–15 minutes)…'
    );
    try {
      const r = await videoCreatorApi.post('/v1/modules/contacts/videos/generate', {
        notes: genNotes || undefined,
      });
      const jobId = r.data?.data?.id;
      if (!jobId) throw new Error('No job id returned');
      const job = await pollJob(jobId);
      setGenMsg(`Created ${job.version || job.result?.version || 'new version'} (product UI recording).`);
      setGenNotes('');
      await loadHelp();
    } catch (e) {
      setGenErr(e.response?.data?.message || e.message || 'Generate failed');
      setGenMsg('');
    } finally {
      setGenerating(false);
    }
  };

  const walkSteps = help?.walkthrough?.steps || [];
  const generatedVersions = help?.generated?.versions || [];
  const catalogOnly = (help?.videos || []).filter((v) => v.source === 'catalog' || (!v.version && v.source !== 'generated'));

  return (
    <Box sx={{ p: 2, maxWidth: 960 }}>
      <Typography variant="h5" gutterBottom>
        Help · Contacts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Module help from <code>erp4c-video-creator</code>. Videos tab lists versioned auto-generated demos — regenerate
        after Contacts changes (e.g. months later) to produce the latest version.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tab label="Read Me" />
        <Tab label="Videos" />
        <Tab label="Walkthrough" />
      </Tabs>

      {loading && (
        <Box py={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && help && (
        <>
          <TabPanel value={tab} index={0}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SimpleMarkdown text={help.readme?.markdown} />
              <Divider sx={{ my: 2 }} />
              <Button variant="contained" onClick={() => navigate('/contacts')}>
                Open Contacts
              </Button>
            </Paper>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Stack spacing={2}>
              {help.can_auto_generate && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Generate new version
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Records a <strong>full narrated Contacts tutorial</strong> from the live UI: list &amp; filters,
                    create contact (mandatory fields explained), link a company account, and attach a document. Saves
                    as the next version (v1, v2, …). Often takes 8–15 minutes.
                  </Typography>
                  <TextField
                    label="Notes (optional)"
                    fullWidth
                    size="small"
                    margin="dense"
                    value={genNotes}
                    onChange={(e) => setGenNotes(e.target.value)}
                    placeholder="e.g. After contact roles UI update — Jul 2026"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Video generation moved to <strong>Administration → Help Setup</strong>. This page is kept for
                    reference only.
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Button variant="outlined" disabled>
                      Use Help Setup to generate
                    </Button>
                  </Stack>
                  {genMsg && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      {genMsg}
                    </Alert>
                  )}
                  {genErr && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {genErr}
                    </Alert>
                  )}
                </Paper>
              )}

              <Typography variant="subtitle2" color="text.secondary">
                Versioned demos
                {help.generated?.latest_version ? ` · latest ${help.generated.latest_version}` : ''}
              </Typography>

              {generatedVersions.length === 0 ? (
                <Alert severity="info">
                  No auto-generated versions yet. Click <strong>Generate Contacts video</strong> above.
                </Alert>
              ) : (
                generatedVersions.map((v) => (
                  <Paper key={v.id || v.version} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {v.title || `Contacts demo`}
                      </Typography>
                      <Chip size="small" color="primary" label={v.version} />
                      {v.is_latest && <Chip size="small" color="success" label="latest" />}
                      {v.content_stale && (
                        <Chip size="small" color="warning" label="help content changed — regenerate" />
                      )}
                      <Chip size="small" variant="outlined" label={v.status || 'ready'} />
                      {v.duration_label && <Chip size="small" variant="outlined" label={v.duration_label} />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {v.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                      {v.branding ? ` · ${v.branding}` : ''}
                      {v.content_fingerprint ? ` · content ${v.content_fingerprint}` : ''}
                    </Typography>
                    {v.url ? (
                      <Box
                        component="video"
                        controls
                        src={mediaSrc(v.url)}
                        sx={{ width: '100%', maxHeight: 420, bgcolor: '#0f172a', borderRadius: 1 }}
                      />
                    ) : (
                      <Alert severity="warning">File missing for this version.</Alert>
                    )}
                  </Paper>
                ))
              )}

              {catalogOnly.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
                    Catalog / manual entries
                  </Typography>
                  {catalogOnly.map((v) => (
                    <Paper key={v.id} variant="outlined" sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {v.title}
                        </Typography>
                        <Chip size="small" label={v.status || 'planned'} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {v.description}
                      </Typography>
                      {v.url && (
                        <Button href={mediaSrc(v.url)} target="_blank" rel="noopener noreferrer" sx={{ mt: 1 }}>
                          Open
                        </Button>
                      )}
                    </Paper>
                  ))}
                </>
              )}
            </Stack>
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {help.walkthrough?.title || 'Walkthrough'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {help.walkthrough?.description}
              </Typography>
              {walkSteps.length === 0 ? (
                <Alert severity="info">No walkthrough steps defined yet.</Alert>
              ) : (
                <Stepper orientation="vertical" nonLinear activeStep={-1}>
                  {walkSteps.map((s) => (
                    <Step key={s.n || s.title} expanded>
                      <StepLabel>
                        {s.n != null ? `${s.n}. ` : ''}
                        {s.title}
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {s.body}
                        </Typography>
                        {s.route && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(String(s.route).replace(':id', ''))}
                          >
                            Go to {s.route}
                          </Button>
                        )}
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              )}
              <Divider sx={{ my: 2 }} />
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Auto-generate versioned video"
                    secondary="Help → Contacts → Videos → Generate Contacts video (worker in erp4c-video-creator)"
                  />
                </ListItem>
              </List>
            </Paper>
          </TabPanel>
        </>
      )}
    </Box>
  );
}
